import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { delay, filter, firstValueFrom } from 'rxjs';

import { UserState } from '@hiptraveler/data-access/user';
import { AuthDialogActionService } from '@hiptraveler/dialogs/auth-dialog';
import { ExperienceFinderStateService } from '@hiptraveler/features/experience-finder';
import { clientVID, currentLang, getWindowRef, iframeWindowFixedTop, isBrandIframeWidget, isIframe, isWidget, isWidgetReturn, ScrollListenerService, setFavoritesValue, setWidgetReturnValue, WidgetStateService } from '@hiptraveler/common';
import { WidgetObserverStateService } from './widget-observer-state.service';
import { manageWidgetSignOutFlag } from './functions';
import { WindowCommunication } from './model';

@Injectable()
export class WidgetObserverService {

  constructor(
    private router: Router,
    private store: Store,
    private widgetState: WidgetStateService,
    private scrollListener: ScrollListenerService,
    private authDialog: AuthDialogActionService,
    private stateService: ExperienceFinderStateService,
    private service: WidgetObserverStateService
  ) { }

  async observe(): Promise<void> {

    if (!isIframe()) return;

    this.toggleIframeWidgetParentNavbar();
    this.setupAuthSuccessPostMessage();
    this.initializeSignOutObserver();
    this.widgetActionsObserver();

    await firstValueFrom(this.widgetState.brandIframeWidget$.pipe(filter(Boolean)));

    console.info('@@@ ', 'iframe-widget detected.');

    this.initializeUrlSyncPostMessage();
  }

  /**
   * Sets up a listener for authenticated access tokens and posts a success message to the parent window.
   */
  private setupAuthSuccessPostMessage(): void {
    this.service.listenForAuthenticatedAccessToken((accessToken: string | null) => {
      const postMessage = {
        id: accessToken,
        vId: clientVID(),
        action: isWidgetReturn() ? 'tripplanner' : 'noaction',
        message: 'ht_success'
      };
      console.info('@@@ postMessage', postMessage);
      getWindowRef()?.top?.postMessage(postMessage, '*');
    });
  }

  /**
   * Toggles the host page’s navbar/iframe position for the widget based on route and scroll.
   */
  private toggleIframeWidgetParentNavbar(): void {
    this.scrollListener.scrollPosition$.pipe( // When scrollTop is 112
      delay(0),
    ).subscribe((scrollTop: number) => {
      if (window.location.pathname !== `/${currentLang()}`) return;
      if (scrollTop > 112) {
        iframeWindowFixedTop(true);
      } else {
        iframeWindowFixedTop(false);
      }
    });
  }

  /**
   * Initializes the sign-out observer: when sign-out completes successfully,
   * it flags the widget, notifies the top window, and then clears the flag.
   */
  private initializeSignOutObserver(): void {
    this.service.registerAuthSignOutHandler(() => {
      manageWidgetSignOutFlag('prevent');
      const postMessage = {
        vId: clientVID(),
        message: 'ht_logout_success',
      };
      console.info('@@@ postMessage', postMessage);
      getWindowRef()?.top?.postMessage(postMessage, '*');
      manageWidgetSignOutFlag('clear');
    });
  }

  /**
   * Initializes URL synchronization by listening for URL changes and
   * posting a `ht_sync_url` message with the updated hash to the parent window.
   */
  private initializeUrlSyncPostMessage(): void {
    this.service.setupUrlSyncListener((url: string) => {
      const postMessage = {
        message: 'ht_sync_url',
        hash: url
      };
      console.info('@@@ postMessage', postMessage);
      getWindowRef()?.top?.postMessage(postMessage, '*');

      setTimeout(() => {
        iframeWindowFixedTop(url !== `/${currentLang()}`);
      });
    });
  }

  private widgetActionsObserver(): void {
    getWindowRef()?.addEventListener('message', (event: MessageEvent<WindowCommunication>) => {

      const { message = '', section = '' } = event.data;

      if (message) {
        console.info('@@@ DC: entry', event);
      }

      this.deepLinkCommunication(event);

      if (isBrandIframeWidget() && !isWidget() && message === 'ht_sign_in_popup') {
        setWidgetReturnValue(false);
        this.authDialog.open();
        return;
      }

      if (isBrandIframeWidget() && message === 'ht_profile_redirect' && [ 'dashboard', 'my-collections#favorites' ].includes(section)) {
        const profileId = this.store.selectSnapshot(UserState.profileId);
        this.router.navigate([ `/${currentLang()}/profile/${profileId}/${section}` ]);
        return;
      }

      if (isBrandIframeWidget() && message !== 'ht_exp_finder_popup') {
        const profileId = this.store.selectSnapshot(UserState.profileId);

        if (profileId && message === 'ht_pr_vw') {
          this.router.navigate([`/${currentLang()}/profile/${profileId}/dashboard`]);
        }
        if (!profileId && message === 'ht_pr_vw' && !isWidget()) {
          this.authDialog.open();
        }
        return;
      }
    });
  }

  private deepLinkCommunication(event: MessageEvent<WindowCommunication>): void {

    const { message = '', section = '' } = event.data;
    if (message !== "ht_exp_finder_popup" || section.trim() === '') return;

    console.info('@@@ Deep-link communication ::', event);

    setWidgetReturnValue(false);
    isWidget() && this.service.trackWidgetPostMessageEvent();

    if (section == "favs_interests" || section == "favs") {
      setFavoritesValue(true);
    } else {
      setFavoritesValue(false);
    }

    if (section == "menu") {
      if (isWidget()) {
        setWidgetReturnValue(true);
        return this.stateService.dialogView$$.next('main-menu'); // not authenticated user menu. default view
      }
      this.service.openPlanTripExperienceFinderPanel();
    }

    if (section == "user-menu") {
      if (isWidget()) {
        setWidgetReturnValue(false);
        return this.stateService.dialogView$$.next('main-menu'); // authenticated user menu
      }
      this.service.openPlanTripExperienceFinderPanel();
    }

    if (section == "login" && isWidget()) {
      setWidgetReturnValue(false);
      this.stateService.dialogView$$.next('auth-menu');
    }

    if (section == "interests" || section == "favs_interests") {
      setWidgetReturnValue(true);
      this.stateService.dialogView$$.next('travel-style'); // experience finder carousal screen
    }

    if (section == "tripPlanner" || section == "favs") {
      setWidgetReturnValue(true);
      this.stateService.dialogView$$.next('plan-trip'); // new trip screen
    }
  }

}
